import React, { useRef, useState } from "react";
import NoControlsVideo from "./NoControlsVideo";

/* Only a big desktop screen is worth the 4K upgrade. On a phone the master
   (30MB .mov / 50MB .mp4) just competes with the rest of the page for
   bandwidth, and Android Chrome would cross-fade to it on a canplaythrough
   guess and then stall mid-loop — the hero froze. */
const wants4k = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia?.("(min-width: 1024px) and (hover: hover) and (pointer: fine)").matches;

/* Progressive hero video: a 3.6MB 720p rendition (plus a poster frame) paints
   immediately; on desktop the 4K masters (29-48MB) stream in the background
   only once the 720p is already playing, then cross-fade in at the matching
   timestamp. Renders two absolutely-positioned videos — the parent must be
   positioned. The 720p goes through NoControlsVideo, so Low Power Mode never
   paints a play button over the hero and in-app browsers get the poster. */
const ProgressiveHeroVideo = () => {
  const lowRef = useRef(null);
  const hiRef = useRef(null);
  const swappedRef = useRef(false);
  const [loadHi, setLoadHi] = useState(false);
  const [hiVisible, setHiVisible] = useState(false);

  const handleHiReady = () => {
    if (swappedRef.current) return; // canplaythrough refires on loops/seeks
    swappedRef.current = true;
    const lo = lowRef.current;
    const hi = hiRef.current;
    if (lo && hi) hi.currentTime = lo.currentTime;
    hi?.play().catch(() => {});
    setHiVisible(true);
    // 720p keeps playing underneath until the fade finishes, then stops
    setTimeout(() => lowRef.current?.pause(), 800);
  };

  return (
    <>
      <NoControlsVideo
        ref={lowRef}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        loop
        poster="/assets/landingpage-poster.jpg"
        src="/assets/landingpage-720.mp4"
        onPlaying={() => {
          if (wants4k()) setLoadHi(true);
        }}
      />
      {loadHi && (
        <video
          ref={hiRef}
          className="absolute inset-0 w-full h-full object-cover"
          muted
          loop
          playsInline
          preload="auto"
          style={{ opacity: hiVisible ? 1 : 0, transition: "opacity 600ms ease" }}
          onCanPlayThrough={handleHiReady}
        >
          <source src="/assets/landingpage.mov" type='video/mp4; codecs="hvc1"' />
          <source src="/assets/landingpage.mp4" type="video/mp4" />
        </video>
      )}
    </>
  );
};

export default ProgressiveHeroVideo;
